import { isAPIError, isNetworkError, isTimeoutError, getErrorDetails } from "./error-types";

export interface ErrorLogEntry {
  id: string;
  timestamp: string;
  errorType: string;
  message: string;
  stack?: string;
  details: Record<string, unknown>;
  severity: "low" | "medium" | "high" | "critical";
  context?: {
    component?: string;
    action?: string;
  };
}

const MAX_LOGS = 1000;
const STORAGE_KEY = "hub_web:error_logs";
const MAX_PERSISTED = 100;

class ErrorLoggingServiceImpl {
  private logs: ErrorLogEntry[] = [];

  constructor() {
    this.loadFromStorage();
  }

  logError(error: unknown, context?: ErrorLogEntry["context"]): void {
    const entry: ErrorLogEntry = {
      id: `err-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      timestamp: new Date().toISOString(),
      errorType: this.getErrorType(error),
      message: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
      details: getErrorDetails(error),
      severity: this.getSeverity(error),
      context,
    };

    this.addEntry(entry);

    if (import.meta.env.DEV) {
      console.error(
        `[${entry.severity.toUpperCase()}] ${entry.errorType}:`,
        entry.message,
        entry.details,
      );
    }
  }

  logWarning(
    message: string,
    details?: Record<string, unknown>,
    context?: ErrorLogEntry["context"],
  ): void {
    this.addEntry({
      id: `warn-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      timestamp: new Date().toISOString(),
      errorType: "WARNING",
      message,
      details: details ?? {},
      severity: "low",
      context,
    });
  }

  getErrorLogs(filter?: Partial<ErrorLogEntry>): ErrorLogEntry[] {
    if (!filter) return [...this.logs];

    return this.logs.filter((log) => {
      if (filter.errorType && log.errorType !== filter.errorType) return false;
      if (filter.severity && log.severity !== filter.severity) return false;
      if (filter.context?.component && log.context?.component !== filter.context.component)
        return false;
      return true;
    });
  }

  clearLogs(): void {
    this.logs = [];
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* noop */
    }
  }

  exportLogs(): string {
    return JSON.stringify(this.logs, null, 2);
  }

  private addEntry(entry: ErrorLogEntry): void {
    this.logs.unshift(entry);
    if (this.logs.length > MAX_LOGS) this.logs = this.logs.slice(0, MAX_LOGS);
    this.saveToStorage();
  }

  private getSeverity(error: unknown): ErrorLogEntry["severity"] {
    if (isAPIError(error)) {
      if (error.status >= 500) return "critical";
      if (error.status >= 400) return "high";
      return "medium";
    }
    if (isNetworkError(error)) return "high";
    if (isTimeoutError(error)) return "medium";
    return "medium";
  }

  private getErrorType(error: unknown): string {
    if (isAPIError(error)) return "API_ERROR";
    if (isNetworkError(error)) return "NETWORK_ERROR";
    if (isTimeoutError(error)) return "TIMEOUT_ERROR";
    if (error instanceof Error) return error.constructor.name;
    return "UNKNOWN_ERROR";
  }

  private saveToStorage(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.logs.slice(0, MAX_PERSISTED)));
    } catch {
      /* noop */
    }
  }

  private loadFromStorage(): void {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) this.logs = parsed;
      }
    } catch {
      /* noop */
    }
  }
}

export const errorLoggingService = new ErrorLoggingServiceImpl();

export const logError = (error: unknown, context?: ErrorLogEntry["context"]): void =>
  errorLoggingService.logError(error, context);

export const logWarning = (
  message: string,
  details?: Record<string, unknown>,
  context?: ErrorLogEntry["context"],
): void => errorLoggingService.logWarning(message, details, context);
